import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import { OrderData } from "types"

interface UseOrderDataParams {
  orderUuid: string
  deviceId: string
  enabled?: boolean
}

export const useOrderData = ({
  orderUuid,
  deviceId,
  enabled = true,
}: UseOrderDataParams) => {
  const {
    data: orderData,
    isPending,
    isLoading,
    error,
    refetch,
  } = useQuery<OrderData>({
    queryKey: ["order", orderUuid],
    queryFn: async () => {
      if (!orderUuid) throw new Error("Order UUID is required")
      if (!deviceId) throw new Error("Device ID is required")

      const response = await axios.get(
        `/api/registration/order/${orderUuid}/?device_id=${deviceId}`
      )
      return response.data
    },
    enabled: enabled && !!orderUuid && !!deviceId,
    retry: 3,
    staleTime: 30 * 1000, // 30 seconds (order data changes frequently)
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  })

  return {
    // Raw data
    orderData,

    // Extracted fields for convenience
    orderId: orderData?.id,
    orderUuid: orderData?.uuid,
    total: orderData?.total ?? 0,
    subtotal: orderData?.amount_subtotal ?? 0,
    discount: orderData?.amount_discount ?? 0,
    discountCode: orderData?.discount_code,
    tickets: orderData?.ticket_set ?? [],

    // Query state
    isPending,
    isLoading,
    error,
    refetch,
  }
}
